<template>
  <div class="container" v-if="flodesk">
    <div class="row">
      <div class="col-12">
        <TopNav class="mt-4" />
        <img class="mt-3" :srcset="`${logo} 3x`" />
        <p class="mt-4 mb-4">
          {{ $t('SETTINGS.TEXT.CONNECT_YOUR_FLODESK_ACCOUNT') }}
        </p>
        <HMActivatedTag
          v-if="!isLoading && flodesk && flodesk.is_connected"
          :label="$t('SETTINGS.LABEL.CONNECTED')"
          class="my-3"
        />
      </div>
      <div class="col-12">
        <div class="form-group">
          <div v-if="flodesk && flodesk.is_connected">
            <hr class="mt-0 mb-4" />
            <HMButton
              color="primary"
              @click="remove"
              :loading="inProgress"
              :disabled="inProgress"
            >
              {{ $t('SETTINGS.BUTTON.DISCONNECT_FROM_FLODESK') }}
            </HMButton>
          </div>
          <div v-else class="mt-3">
            <HMButton
              color="primary"
              @click="connect"
              :loading="inProgress"
              :disabled="inProgress"
            >
              {{ $t('SETTINGS.BUTTON.CONNECT_FLODESK') }}
            </HMButton>
          </div>
        </div>
      </div>
    </div>
    <div class="row mb-4" v-if="flodesk && flodesk.is_connected">
      <div class="col-12 col-md-6">
        <h4>{{ $t('SETTINGS.LABEL.DOUBLE_OPT_IN') }}</h4>
        <div>
          {{ $t('SETTINGS.TEXT.FLODESK_ENABLE_DOUBLE_OPT_IN') }}
        </div>
      </div>
      <div class="col-12 col-md-6 text-right">
        <HMSwitch v-model="flodesk.flodesk_double_optin" @change="update" />
      </div>
    </div>
    <div class="row" v-if="flodesk && flodesk.is_connected && flodeskSegments">
      <div class="col-12 col-md-6">
        <h4>{{ $t('SETTINGS.LABEL.SELECT_SEGMENT') }}</h4>
        <div>
          {{ $t('SETTINGS.TEXT.SELECT_DEFAULT_FLODESK_SEGMENT') }}
        </div>
      </div>
      <div class="col-12 col-md-6">
        <v-select
          class="w-100"
          :options="flodeskSegments"
          label="name"
          code="id"
          v-model="flodesk.flodesk_default_segment_id"
          @input="update"
          :reduce="segment => segment.id"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav.vue';
import { errorToMessage } from '@/core/utils/errors';
import { mapGetters } from 'vuex';

export default {
  name: 'FlodeskIntegration',
  components: {
    TopNav,
  },
  computed: {
    ...mapGetters({
      flodesk: 'setting/flodesk',
      flodeskSegments: 'setting/flodeskSegments',
      isUsingFlodesk: 'setting/isUsingFlodesk',
    }),
    redirectUri() {
      return process.env.VUE_APP_FLODESK_REDIRECT_URL;
    },
  },
  data() {
    return {
      logo: '/assets/images/integrations/flodesk.png',
      isLoading: true,
      inProgress: false,
    };
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/getFlodesk');
        if (this.flodesk.is_connected) {
          await this.$store.dispatch('setting/getFlodeskSegments');
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load flodesk information',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    async update() {
      this.inProgress = true;
      try {
        await this.$store.dispatch('setting/updateFlodesk', this.flodesk);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.FLODESK_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to update flodesk information',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
      this.loadData();
    },
    async remove() {
      this.flodesk.disconnect = true;
      await this.update();
      await this.loadData();
    },
    connect() {
      const responseType = 'code';
      const clientId = process.env.VUE_APP_FLODESK_CLIENT_ID;
      const redirectURL = encodeURIComponent(this.redirectUri);
      this.inProgress = false;
      window.location = `https://api.flodesk.com/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&redirect_uri=${redirectURL}&scope=all&state=92384239623`;
    },
    checkForOAuthCode() {
      const code = this.$route.query.code;
      if (!code) return;

      const query = { ...this.$route.query };
      delete query.code;
      this.$router.replace({ query });

      this.flodesk.flodesk_auth_code = code;
      this.flodesk.redirect_uri = this.redirectUri;
      this.update();
    },
  },
  async mounted() {
    await this.loadData();
    this.checkForOAuthCode();
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.info-box {
  border-radius: $border-radius;
  background-color: lighten($blue, 30%);
  padding: 0.5rem;

  .info {
  }
}
</style>
